import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { HiOutlinePlus } from "react-icons/hi";
import { IoSearchOutline } from "react-icons/io5";
import { TbRefresh } from "react-icons/tb";
import Pagination from "../../Component/Pagination";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import {
  buildEmployeeSelectOptions,
  buildSimpleOptions,
  formatDateOnlyIST,
  getDuePillStyle,
} from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";

function TrainingSessionsListPage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [masters, setMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedMaster, setSelectedMaster] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchLookups = async () => {
    try {
      const [employeesResponse, mastersResponse] = await Promise.all([
        hrRecordsApi.fetchEmployeesDropdown(),
        hrRecordsApi.listTrainingMasters({ page: 1, per_page: 200 }),
      ]);

      setEmployees(buildEmployeeSelectOptions(employeesResponse.data || []));
      setMasters(
        buildSimpleOptions(
          mastersResponse.items || [],
          "id",
          "training_name",
        ),
      );
    } catch (error) {
      console.error("Failed to load training lookups:", error);
    }
  };

  const fetchSessions = async (
    currentPage = page,
    currentItemsPerPage = itemsPerPage,
  ) => {
    try {
      setLoading(true);
      const response = await hrRecordsApi.listTrainingSessions({
        page: currentPage,
        per_page: currentItemsPerPage,
        search: searchInput || undefined,
        actual_man_power_id: selectedEmployee?.value || undefined,
        training_master_id: selectedMaster?.value || undefined,
      });
      setSessions(response.items || []);
      setTotalItems(response.total_items || 0);
    } catch (error) {
      console.error("Failed to load training sessions:", error);
      showSnackbar("Failed to load training sessions", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      fetchSessions(page, itemsPerPage);
    }, 350);

    return () => window.clearTimeout(timer);
  }, [page, itemsPerPage, searchInput, selectedEmployee, selectedMaster]);

  const handleRefresh = () => {
    setSearchInput("");
    setSelectedEmployee(null);
    setSelectedMaster(null);
    setPage(1);
    fetchSessions(1, itemsPerPage);
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm("Delete this training session?")) {
      return;
    }

    try {
      await hrRecordsApi.deleteTrainingSession(sessionId);
      showSnackbar("Training session deleted successfully");
      fetchSessions(page, itemsPerPage);
    } catch (error) {
      console.error("Failed to delete training session:", error);
      showSnackbar(
        error?.response?.data?.message || "Failed to delete training session",
        "error",
      );
    }
  };

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Training Sessions</PageBackTitle>
              <div className="data-list-heading-content-right-side hr-records-toolbar">
                <div className="dropdown-container" style={{ minWidth: "230px" }}>
                  <Select
                    options={masters}
                    value={selectedMaster}
                    onChange={(value) => {
                      setSelectedMaster(value);
                      setPage(1);
                    }}
                    placeholder="Filter by Training"
                    isSearchable
                    isClearable
                  />
                </div>
                <div className="dropdown-container" style={{ minWidth: "230px" }}>
                  <Select
                    options={employees}
                    value={selectedEmployee}
                    onChange={(value) => {
                      setSelectedEmployee(value);
                      setPage(1);
                    }}
                    placeholder="Filter by Employee"
                    isSearchable
                    isClearable
                  />
                </div>
                <div className="search-bar-container">
                  <input
                    type="text"
                    className="search-bar"
                    placeholder="Search sessions..."
                    value={searchInput}
                    onChange={(event) => {
                      setSearchInput(event.target.value);
                      setPage(1);
                    }}
                  />
                  <IoSearchOutline className="search-bar-icon" />
                </div>
                <Tooltip title="New Training Session" arrow>
                  <button
                    className="new-button"
                    onClick={() => navigate("/add-hr-training-session")}
                  >
                    <HiOutlinePlus className="new-plus-icon" /> New Session
                  </button>
                </Tooltip>
                <Tooltip title="Refresh" arrow>
                  <button className="refresh-button" onClick={handleRefresh}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader"></div>
                    <div>Loading...</div>
                  </div>
                ) : sessions.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th>SR. NO.</th>
                        <th>SESSION CODE</th>
                        <th>TRAINING</th>
                        <th>STANDARD</th>
                        <th>DATES</th>
                        <th>STATUS</th>
                        <th>TRAINER</th>
                        <th>PARTICIPANTS</th>
                        <th>ATTACHMENTS</th>
                        <th>CREATED AT</th>
                        <th>ACTION</th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {sessions.map((session, index) => (
                        <tr key={session.id} className="hoverable-row">
                          <td>{(page - 1) * itemsPerPage + index + 1}</td>
                          <td>{session.session_code || "NA"}</td>
                          <td>{session.training_name || "NA"}</td>
                          <td>{session.standard_body || "NA"}</td>
                          <td>
                            {formatDateOnlyIST(session.start_date)}
                            <br />
                            <span style={{ color: "#68768a", fontSize: "12px" }}>
                              to {formatDateOnlyIST(session.end_date)}
                            </span>
                          </td>
                          <td className="center-alignment">
                            <span
                              className="hr-records-pill"
                              style={getDuePillStyle(session.session_status)}
                            >
                              {session.session_status || "NA"}
                            </span>
                          </td>
                          <td>{session.trainer_name || "NA"}</td>
                          <td>
                            {session.participant_count || 0}
                            <br />
                            <span style={{ color: "#68768a", fontSize: "12px" }}>
                              Present: {session.present_count || 0}
                            </span>
                          </td>
                          <td>
                            Photos: {session.photograph_count || 0}
                            <br />
                            <span style={{ color: "#68768a", fontSize: "12px" }}>
                              Sheets: {session.attendance_sheet_count || 0}
                            </span>
                          </td>
                          <td>
                            {session.created_at
                              ? new Date(session.created_at)
                                  .toUTCString()
                                  .split("GMT")[0]
                                  .trim() + " IST"
                              : "NA"}
                          </td>
                          <td className="center-alignment">
                            <div className="hr-records-inline-actions">
                              <button
                                className="button-bg"
                                onClick={() =>
                                  navigate(`/hr-training-session-view/${session.id}`)
                                }
                              >
                                View
                              </button>
                              <Link to={`/edit-hr-training-session/${session.id}`}>
                                <button className="button-bg">Edit</button>
                              </Link>
                              
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">No training sessions found</div>
                  </div>
                )}
              </div>

              <Pagination
                key={`${selectedEmployee?.value || "all"}-${selectedMaster?.value || "all"}-${searchInput}`}
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={(currentPage, perPage) => {
                  setPage(currentPage);
                  setItemsPerPage(perPage);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default TrainingSessionsListPage;
